import { AnimatePresence, motion, LayoutGroup } from "framer-motion";
import React, { useState, useEffect, useRef, useCallback } from "react";

import img1 from "../assets/demos/2fa3e9e0141a732cc4d1bef7a4c688acd59496de.jpg";
import img6 from "../assets/demos/3e9db6412e8ef7498743acbc06de16381a2c6c94.png";
import img3 from "../assets/demos/683c8cc3d46e50ca484138842907726bcabc3255.png";
import img4 from "../assets/demos/72f2064b7bd11ad5029aa43aa70fd795001d2e46.jpg";
import img5 from "../assets/demos/90631c833b5017e58ad5772319b20746a04201f3.png";
import img2 from "../assets/demos/9a27f40074a3887fcd4743a232ef0845665b7a68.jpg";
import img7 from "../assets/demos/df207874eeb7118e71ca106e7054e91e649a4e88.jpg";
import vid1 from "../assets/demos/IMG_2241.mp4";
import vid4 from "../assets/demos/IMG_2245.mp4";
import vid3 from "../assets/demos/IMG_2924.mp4";
import vid2 from "../assets/demos/IMG_2944.mp4";

const FRAME_CHANGE_DELAY = 10000; // ms

const frames = [
  [
    { id: "media1", type: "image", src: img1, colSpan: 2, rowSpan: 1 },
    { id: "media2", type: "image", src: img2, colSpan: 1, rowSpan: 1 },
    { id: "media3", type: "video", src: vid1, colSpan: 1, rowSpan: 2 },
    { id: "media4", type: "video", src: vid4, colSpan: 2, rowSpan: 2 },
    { id: "media5", type: "image", src: img4, colSpan: 1, rowSpan: 1 },
    { id: "media6", type: "image", src: img7, colSpan: 2, rowSpan: 1 },
  ],
  [
    { id: "media1", type: "image", src: img6, colSpan: 1, rowSpan: 1 },
    { id: "media2", type: "video", src: vid2, colSpan: 3, rowSpan: 2 },
    { id: "media4", type: "image", src: img3, colSpan: 1, rowSpan: 2 },
    { id: "media6", type: "video", src: vid3, colSpan: 2, rowSpan: 1 },
    { id: "media3", type: "image", src: img5, colSpan: 1, rowSpan: 1 },
  ],
];

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gridTemplateRows: "repeat(3, 1fr)",
  aspectRatio: "7/5",
  position: "relative",
  marginBottom: "10px",
};

// Media Cache Manager
class MediaCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  async preloadImage(src) {
    if (this.cache.has(src)) {
      return this.cache.get(src);
    }

    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.cache.set(src, img);
        this.loadingPromises.delete(src);
        resolve(img);
      };
      img.onerror = () => {
        this.loadingPromises.delete(src);
        reject(new Error(`Failed to load image: ${src}`));
      };
      img.src = src;
    });

    this.loadingPromises.set(src, promise);
    return promise;
  }

  async preloadVideo(src) {
    if (this.cache.has(src)) {
      return this.cache.get(src);
    }

    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("x5-playsinline", "true");
      video.crossOrigin = "anonymous";

      let hasResolved = false;

      const handleCanPlay = () => {
        if (!hasResolved) {
          hasResolved = true;
          this.cache.set(src, video);
          this.loadingPromises.delete(src);
          cleanup();
          resolve(video);
        }
      };

      const handleLoadedData = () => {
        if (!hasResolved) {
          hasResolved = true;
          this.cache.set(src, video);
          this.loadingPromises.delete(src);
          cleanup();
          resolve(video);
        }
      };

      const handleError = (e) => {
        if (!hasResolved) {
          hasResolved = true;
          this.loadingPromises.delete(src);
          cleanup();
          console.warn(`Video preload failed for ${src}:`, e);
          // Still resolve with the video element for fallback
          resolve(video);
        }
      };

      const cleanup = () => {
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("error", handleError);
      };

      // Add multiple event listeners for better mobile compatibility
      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("error", handleError);

      // Set timeout as fallback for mobile devices
      setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true;
          this.cache.set(src, video);
          this.loadingPromises.delete(src);
          cleanup();
          resolve(video);
        }
      }, 5000); // 5 second timeout

      video.src = src;
    });

    this.loadingPromises.set(src, promise);
    return promise;
  }

  isLoaded(src) {
    return this.cache.has(src);
  }

  get(src) {
    return this.cache.get(src);
  }
}

// Global media cache instance
const mediaCache = new MediaCache();

// Optimized Image Component with cache support
const OptimizedImage = ({ src, alt, style, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView) {
      // Check if image is already cached
      if (mediaCache.isLoaded(src)) {
        setIsLoaded(true);
      } else {
        // Preload if not cached
        mediaCache
          .preloadImage(src)
          .then(() => {
            setIsLoaded(true);
          })
          .catch(console.error);
      }
    }
  }, [isInView, src]);

  return (
    <div ref={imgRef} style={style} className={className}>
      {isInView && (
        <>
          {!isLoaded && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)",
                backgroundSize: "20px 20px",
                backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                animation: "shimmer 1.5s infinite linear",
              }}
            />
          )}
          <img
            src={src}
            alt={alt}
            style={{
              ...style,
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
            loading="lazy"
            decoding="async"
          />
        </>
      )}
    </div>
  );
};

// Optimized Video Component with cache support
const OptimizedVideo = ({ src, style, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef();
  const videoRef = useRef();
  const playAttempted = useRef(false);

  const attemptAutoplay = useCallback(async () => {
    if (videoRef.current && !playAttempted.current && isLoaded) {
      playAttempted.current = true;
      try {
        // Multiple autoplay strategies
        videoRef.current.muted = true; // Ensure muted
        videoRef.current.volume = 0; // Extra ensure volume is 0

        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          setIsPlaying(true);
          console.log("Autoplay successful");
        }
      } catch (error) {
        console.log("Autoplay failed, will retry on user interaction:", error);
        setIsPlaying(false);

        // Add click listener for user-initiated play
        const handleUserInteraction = async () => {
          try {
            await videoRef.current.play();
            setIsPlaying(true);
            document.removeEventListener("touchstart", handleUserInteraction);
            document.removeEventListener("click", handleUserInteraction);
          } catch (e) {
            console.log("User-initiated play failed:", e);
          }
        };

        document.addEventListener("touchstart", handleUserInteraction, {
          once: true,
        });
        document.addEventListener("click", handleUserInteraction, {
          once: true,
        });
      }
    }
  }, [isLoaded]);

  // More aggressive intersection observer for autoplay
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Try to play when video comes into view
          setTimeout(() => {
            attemptAutoplay();
          }, 200);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }, // Higher threshold for better autoplay success
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [attemptAutoplay]);

  useEffect(() => {
    if (isInView) {
      // Check if video is already cached
      if (mediaCache.isLoaded(src)) {
        setIsLoaded(true);
        setTimeout(() => {
          attemptAutoplay();
        }, 100);
      } else {
        // Preload if not cached
        mediaCache
          .preloadVideo(src)
          .then(() => {
            setIsLoaded(true);
            setTimeout(() => {
              attemptAutoplay();
            }, 200);
          })
          .catch((error) => {
            console.error("Video preload failed:", error);
            setHasError(true);
            setIsLoaded(true);
          });
      }
    }
  }, [isInView, src, attemptAutoplay]);

  const handleVideoError = () => {
    console.error("Video playback error:", src);
    setHasError(true);
  };

  const handleVideoLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    playAttempted.current = false; // Reset play attempt flag
    setTimeout(() => {
      attemptAutoplay();
    }, 100);
  };

  const handleCanPlay = () => {
    if (!playAttempted.current) {
      setTimeout(() => {
        attemptAutoplay();
      }, 50);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  return (
    <div ref={containerRef} style={style} className={className}>
      {isInView && (
        <>
          {!isLoaded && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)",
                backgroundSize: "20px 20px",
                backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                animation: "shimmer 1.5s infinite linear",
              }}
            />
          )}
          {hasError && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "14px",
                textAlign: "center",
                padding: "10px",
              }}
            >
              Video Loading...
            </div>
          )}
          {/* Play indicator for mobile feedback */}
          {isLoaded && !isPlaying && !hasError && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "rgba(0,0,0,0.7)",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "20px",
                zIndex: 10,
                cursor: "pointer",
              }}
              onClick={attemptAutoplay}
            >
              ▶
            </div>
          )}
          <video
            ref={videoRef}
            src={src}
            controls={false} // Remove controls for cleaner autoplay
            muted
            playsInline
            loop
            preload="metadata"
            webkit-playsinline="true"
            x5-playsinline="true"
            autoPlay // Add back autoPlay attribute
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
            onCanPlay={handleCanPlay}
            onPlay={handlePlay}
            onPause={handlePause}
            style={{
              ...style,
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        </>
      )}
    </div>
  );
};

const DemoMedia = () => {
  const [frameIndex, setFrameIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef();

  // Preload all media on component mount
  useEffect(() => {
    const preloadAllMedia = async () => {
      const allMedia = [];

      // Collect all unique media sources
      frames.forEach((frame) => {
        frame.forEach((item) => {
          if (!allMedia.find((media) => media.src === item.src)) {
            allMedia.push(item);
          }
        });
      });

      // Preload all media in background
      const preloadPromises = allMedia.map(async (item) => {
        try {
          if (item.type === "image") {
            await mediaCache.preloadImage(item.src);
          } else {
            await mediaCache.preloadVideo(item.src);
          }
        } catch (error) {
          console.error(`Failed to preload ${item.type}:`, item.src, error);
        }
      });

      await Promise.all(preloadPromises);
    };

    preloadAllMedia();
  }, []);

  // Smart preloading for next frame (backup)
  useEffect(() => {
    const nextFrameIndex = 1 - frameIndex;
    const nextFrameMedia = frames[nextFrameIndex];

    nextFrameMedia.forEach((item) => {
      if (!mediaCache.isLoaded(item.src)) {
        if (item.type === "image") {
          mediaCache.preloadImage(item.src).catch(console.error);
        } else {
          mediaCache.preloadVideo(item.src).catch(console.error);
        }
      }
    });
  }, [frameIndex]);

  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(() => {
        setFrameIndex((i) => 1 - i);
      }, FRAME_CHANGE_DELAY);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovered]);

  return (
    <>
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position:
              0 0,
              0 10px,
              10px -10px,
              -10px 0px;
          }
          100% {
            background-position:
              20px 20px,
              20px 30px,
              30px 10px,
              10px 20px;
          }
        }
      `}</style>

      <div className="relative">
        <div className="max-sm:overflow-x-auto overflow-x-visible max-sm:pb-4">
          <div className="w-full px-[10px] sm:px-[20px] md:px-[44px] mt-[10px] sm:mt-[100px] md:mt-[50px]">
            <div
              style={gridStyle}
              className="gap-[5px] lg:gap-[10px] w-[150%] sm:w-full max-sm:min-w-[150%]"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <LayoutGroup>
                <motion.div
                  className="gap-[5px] lg:gap-[10px]"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gridTemplateRows: "repeat(3, 1fr)",
                  }}
                  layout
                  transition={{
                    layout: { duration: 2, ease: "easeInOut" },
                  }}
                >
                  {frames[frameIndex].map((item) => (
                    <motion.div
                      key={item.id}
                      layout
                      layoutId={item.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 2,
                          ease: "easeOut",
                        },
                      }}
                      transition={{
                        layout: {
                          duration: 2,
                          ease: "easeInOut",
                          type: "spring",
                          stiffness: 100,
                          damping: 20,
                        },
                      }}
                      style={{
                        gridColumn: `span ${item.colSpan}`,
                        gridRow: `span ${item.rowSpan}`,
                        overflow: "hidden",
                        background: "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                      className="rounded-[18px] md:rounded-[25px] lg:rounded-[50px]"
                    >
                      <AnimatePresence>
                        {item.type === "image" ? (
                          <motion.div
                            key={`${item.id}-${item.src}-${frameIndex}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              duration: 1.2,
                              ease: "easeInOut",
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          >
                            <OptimizedImage
                              src={item.src}
                              alt="media"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key={`${item.id}-${item.src}-${frameIndex}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              duration: 1.2,
                              ease: "easeInOut",
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          >
                            <OptimizedVideo
                              src={item.src}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </motion.div>
              </LayoutGroup>
            </div>
          </div>
        </div>
        <div className="text-[#A2A2A2] ml-[44px] font-creato text-2xl font-normal hidden sm:block max-md:text-[18px] max-md:ml-[20px]">
          SF Hacks at SF States, 2025
        </div>
      </div>
    </>
  );
};

export default DemoMedia;
